import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { makeObservable, observable, runInAction } from 'mobx';
import { ModalButtonType, modalService, type ModalButtonOptions, type ModalDialogOptions, ModalWindow, type IModalDialogContent } from '@components/Modal';
import { apiService } from '@services/ApiService';
import { notificationService } from '@services/NotificationService';
import { dateTimeService } from '@services/DateTimeService';
import { Skeleton } from '@components/Skeleton';
import { ImgClock } from '@components/Images/ImgClock';
import { ImgPlusCircle } from '@components/Images/ImgPlusCircle';
import { ImgEye } from '@components/Images/ImgEye';
import { ImgDocumentCheck } from '@components/Images/ImgDocumentCheck';
import { ImgTrash } from '@components/Images/ImgTrash';

import './_note-history-dialog.scss';

export type NoteHistoryDialogProps = {
  noteId: string;
};

type HistorySnapshotSummary = {
  id: string;
  createdAt: Date;
  createdByUser: boolean;
  name: string;
  diffSummary?: string | null;
};

const untitledSnapshot = '(Untitled)';

/** Compact relative timestamp for timeline rows ("just now", "3h ago", "2d ago"). */
function relativeTime(date: Date): string {
  const t = new Date(date).getTime();
  if (isNaN(t)) return '';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return dateTimeService.format(new Date(date), 'DD MMM, YYYY');
}

/** "+124" / "−124" from a server diff string like "+124 chars" / "-124 chars". */
function parseDiff(diffSummary?: string | null): { added: boolean; value: string } | null {
  if (!diffSummary) return null;
  const m = diffSummary.match(/^([+-])(\d+)/);
  if (!m) return null;
  return { added: m[1] === '+', value: m[2] };
}

@observer
export default class NoteHistoryDialog extends React.Component<NoteHistoryDialogProps> implements IModalDialogContent<void> {
  @observable private _snapshots: HistorySnapshotSummary[] = [];
  @observable private _isLoading = true;
  @observable private _isCreating = false;

  constructor(props: NoteHistoryDialogProps){
    super(props);
    makeObservable(this);
  }

  async componentDidMount(){
    await this._loadSnapshots();
  }

  public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    const count = this._snapshots.length;
    const buttons: ModalButtonOptions<void>[] = [
      {
        title: 'New snapshot',
        type: ModalButtonType.Create,
        color: 'success',
        isDisabled: this._isCreating,
        onClick: () => this._createSnapshot()
      },
      {
        title: 'Close',
        type: ModalButtonType.Cancel,
        onClick: () => window.close()
      }
    ];

    return {
      title: 'Note history',
      buttons,
      width: 720,
      footerInfo: this._isLoading
        ? 'Loading…'
        : count === 0
          ? 'No snapshots yet'
          : `${count} snapshot${count === 1 ? '' : 's'}`
    };
  }

  private async _loadSnapshots(): Promise<void>{
    runInAction(() => { this._isLoading = true; });
    try {
      const data = await apiService.get<{ snapshots: HistorySnapshotSummary[] }>(`/api/note/${this.props.noteId}/history`);
      runInAction(() => { this._snapshots = data.snapshots; });
    } finally {
      runInAction(() => { this._isLoading = false; });
    }
  }

  private async _viewSnapshot(snapshot: HistorySnapshotSummary): Promise<void>{
    // Open the read-only snapshot view in a new tab.
    const snapshotUrl = `/snapshot/${this.props.noteId}/${snapshot.id}`;
    const newTab = window.open(snapshotUrl, '_blank');
    if (!newTab) {
      notificationService.error('Please allow popups to view snapshots');
    }
  }

  private async _restoreSnapshot(snapshot: HistorySnapshotSummary): Promise<void>{
    const confirmed = await modalService.showConfirmation(
      'Restore snapshot',
      `Restore the note to “${snapshot.name || untitledSnapshot}”?\n\nYour current content is saved as a snapshot first, so nothing is lost.`,
      { color: 'success', buttonTitle: 'Restore' }
    );
    if (!confirmed) return;

    await apiService.post<{ safetySnapshotId: string }>(`/api/note/${this.props.noteId}/history/${snapshot.id}/restore`);
    notificationService.success('Note restored');
    window.location.reload();
  }

  private async _createSnapshot(): Promise<void>{
    if (this._isCreating) return;
    runInAction(() => { this._isCreating = true; });
    try {
      await apiService.post(`/api/note/${this.props.noteId}/history`);
      notificationService.success('Snapshot created');
      await this._loadSnapshots();
    } finally {
      runInAction(() => { this._isCreating = false; });
    }
  }

  private async _deleteSnapshot(snapshot: HistorySnapshotSummary): Promise<void>{
    const confirmed = await modalService.showConfirmation(
      'Delete snapshot',
      `Delete “${snapshot.name || untitledSnapshot}”? This cannot be undone.`,
      { color: 'danger', buttonTitle: 'Delete' }
    );
    if (!confirmed) return;

    await apiService.delete(`/api/note/${this.props.noteId}/history/${snapshot.id}`);
    notificationService.success('Snapshot deleted');
    await this._loadSnapshots();
  }

  private _renderRow(snap: HistorySnapshotSummary, index: number): JSX.Element {
    const isManual = snap.createdByUser;
    const diff = parseDiff(snap.diffSummary);
    const fullDate = dateTimeService.format(new Date(snap.createdAt), 'DD MMM YYYY, HH:mm:ss');

    return (
      <div
        key={snap.id}
        className={`history-row${isManual ? ' manual' : ' auto'}${index === 0 ? ' latest' : ''}`}
        style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
      >
        <span className="history-node" aria-hidden="true" />
        <div className="history-row-main">
          <div className="history-row-top">
            <span className="history-name" title={snap.name || undefined}>{snap.name || untitledSnapshot}</span>
            {index === 0 && <span className="history-flag">Latest</span>}
            {diff && (
              <span className={`history-diff${diff.added ? ' add' : ' sub'}`}>
                {diff.added ? '+' : '−'}{diff.value}
              </span>
            )}
          </div>
          <div className="history-row-meta">
            <span className="history-type" title={isManual ? 'Created by you' : 'Automatic snapshot'}>
              {isManual ? 'Manual' : 'Auto'}
            </span>
            <span className="history-dot-sep" aria-hidden="true" />
            <span className="history-time" title={fullDate}>{relativeTime(snap.createdAt)}</span>
          </div>
        </div>
        <div className="history-row-actions">
          <button className="history-action" title="View snapshot" aria-label="View snapshot" onClick={() => this._viewSnapshot(snap)}>
            <ImgEye size={15} />
          </button>
          <button className="history-action restore" title="Restore this version" aria-label="Restore this version" onClick={() => this._restoreSnapshot(snap)}>
            <ImgDocumentCheck size={15} />
          </button>
          {isManual && (
            <button className="history-action delete" title="Delete snapshot" aria-label="Delete snapshot" onClick={() => this._deleteSnapshot(snap)}>
              <ImgTrash size={15} />
            </button>
          )}
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    if (this._isLoading) {
      return (
        <div className="history-skeleton" aria-busy="true">
          {[0, 1, 2, 3].map(i => (
            <div className="history-skeleton-row" key={i}>
              <span className="history-skeleton-node" />
              <div className="history-skeleton-lines">
                <Skeleton width="40%" height={14} />
                <Skeleton width="24%" height={11} />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (this._snapshots.length === 0) {
      return (
        <div className="history-empty">
          <div className="history-empty-icon"><ImgClock size={26} /></div>
          <h4>No snapshots yet</h4>
          <p>Snapshots capture the note at a point in time. Create one now, or wait for an automatic one.</p>
          <button className="btn btn-success history-empty-btn" onClick={this._createSnapshot}>
            <ImgPlusCircle size={15} />
            <span>New snapshot</span>
          </button>
        </div>
      );
    }

    return (
      <div className="history-timeline">
        {this._snapshots.map((snap, i) => this._renderRow(snap, i))}
      </div>
    );
  }
}
